/// <amd-module name="TestPlanCommon/RestClient/TestPlanExtended" />

import * as TestPlan from "TestPlanCommon/Contracts/TestPlan";
import * as ExtendedTestPlan from "TestPlanCommon/Contracts/TestPlanExtended";
import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";

export interface ITestPlanExtendedRestClient extends IVssRestClient {
    /**
     * Get Test Case List return those test cases which have all the configuration Ids as mentioned in the optional paramter. If configuration Ids is null, it return all the test cases
     *
     * @param project - Project ID or project name
     * @param planId -
     * @param suiteId -
     * @param testIds -
     * @param configurationIds -
     * @param witFields -
     * @param continuationToken -
     */
    getTestCaseListWithContinuationToken(
        project: string,
        planId: number,
        suiteId: number,
        testIds?: string,
        configurationIds?: string,
        witFields?: string,
        continuationToken?: string,
        expand?: boolean,
        returnIdentityRef?: boolean,
        excludeFlags?: TestPlan.ExcludeFlags,
        isRecursive?: boolean
    ): Promise<ExtendedTestPlan.TestArtifactWithContinuationToken<TestPlan.TestCase>>;
    /**
     * Get all the points inside a suite based on some filters
     *
     * @param project - Project ID or project name
     * @param planId -
     * @param suiteId -
     * @param testPointIds -
     * @param testCaseId -
     * @param continuationToken -
     */
    getPointsListWithContinuationToken(
        project: string,
        planId: number,
        suiteId: number,
        testPointIds?: string,
        testCaseId?: string,
        continuationToken?: string,
        returnIdentityRef?: boolean,
        includePointDetails?: boolean,
        isRecursive?: boolean
    ): Promise<ExtendedTestPlan.TestArtifactWithContinuationToken<TestPlan.TestPoint>>;

    /**
     * Get all the points inside a suite based on some filters
     *
     * @param project - Project ID or project name
     * @param owner - Filter for test plan by owner ID or name
     * @param continuationToken - If the list of plans returned is not complete, a continuation token to query next batch of plans is included in the response header as "x-ms-continuationtoken". Omit this parameter to get the first batch of test plans.
     * @param includePlanDetails - Get all properties of the test plan
     * @param filterActivePlans - Get just the active plans
     */
    getPlansListWithContinuationToken(
        project: string,
        owner?: string,
        continuationToken?: string,
        includePlanDetails?: boolean,
        filterActivePlans?: boolean
    ): Promise<ExtendedTestPlan.TestArtifactWithContinuationToken<TestPlan.TestPlan>>;
}

class TestPlanExtendedRestClient extends RestClientBase implements ITestPlanExtendedRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Get Test Case List return those test cases which have all the configuration Ids as mentioned in the optional paramter. If configuration Ids is null, it return all the test cases
     *
     * @param project - Project ID or project name
     * @param planId -
     * @param suiteId -
     * @param testIds -
     * @param configurationIds -
     * @param witFields -
     * @param continuationToken -
     */
    public async getTestCaseListWithContinuationToken(
        project: string,
        planId: number,
        suiteId: number,
        testIds?: string,
        configurationIds?: string,
        witFields?: string,
        continuationToken?: string,
        expand?: boolean,
        returnIdentityRef?: boolean,
        excludeFlags?: TestPlan.ExcludeFlags,
        isRecursive?: boolean
    ): Promise<ExtendedTestPlan.TestArtifactWithContinuationToken<TestPlan.TestCase>> {
        const queryValues: any = {
            testIds: testIds,
            configurationIds: configurationIds,
            witFields: witFields,
            continuationToken: continuationToken,
            expand: expand,
            returnIdentityRef: returnIdentityRef,
            excludeFlags: excludeFlags,
            isRecursive: isRecursive
        };

        return this.beginRequest<Response>({
            apiVersion: "5.1-preview.2",
            routeTemplate: "{project}/_apis/testplan/Plans/{planId}/Suites/{suiteId}/TestCase/{testCaseIds}",
            routeValues: {
                project: project,
                planId: planId,
                suiteId: suiteId
            },
            queryParams: queryValues,
            returnRawResponse: true
        }).then((response: Response) => {
            const continuationToken = response.headers.get(continuationTokenHeaderKey);
            return response.text().then((r) => {
                return {
                    results: deserializeVssJsonObject<TestPlan.TestCase[]>(r),
                    continuationToken: continuationToken
                } as ExtendedTestPlan.TestArtifactWithContinuationToken<TestPlan.TestCase>;
            });
        });
    }

    /**
     * Get all the points inside a suite based on some filters
     *
     * @param project - Project ID or project name
     * @param planId -
     * @param suiteId -
     * @param testPointIds -
     * @param testCaseId -
     * @param continuationToken -
     * @param isRecursive -
     */
    public async getPointsListWithContinuationToken(
        project: string,
        planId: number,
        suiteId: number,
        testPointIds?: string,
        testCaseId?: string,
        continuationToken?: string,
        returnIdentityRef?: boolean,
        includePointDetails?: boolean,
        isRecursive?: boolean
    ): Promise<ExtendedTestPlan.TestArtifactWithContinuationToken<TestPlan.TestPoint>> {
        const queryValues: any = {
            testPointIds: testPointIds,
            testCaseId: testCaseId,
            continuationToken: continuationToken,
            returnIdentityRef: returnIdentityRef,
            includePointDetails: includePointDetails,
            isRecursive: isRecursive
        };

        return this.beginRequest<Response>({
            apiVersion: "5.1-preview.2",
            routeTemplate: "{project}/_apis/testplan/Plans/{planId}/Suites/{suiteId}/TestPoint/{pointIds}",
            routeValues: {
                project: project,
                planId: planId,
                suiteId: suiteId
            },
            queryParams: queryValues,
            returnRawResponse: true
        }).then((response: Response) => {
            const continuationToken = response.headers.get(continuationTokenHeaderKey);
            return response.text().then((r) => {
                return {
                    results: deserializeVssJsonObject<TestPlan.TestPoint[]>(r),
                    continuationToken: continuationToken
                } as ExtendedTestPlan.TestArtifactWithContinuationToken<TestPlan.TestPoint>;
            });
        });
    }

    public async getPlansListWithContinuationToken(
        project: string,
        owner?: string,
        continuationToken?: string,
        includePlanDetails?: boolean,
        filterActivePlans?: boolean
    ): Promise<ExtendedTestPlan.TestArtifactWithContinuationToken<TestPlan.TestPlan>> {
        const queryValues: any = {
            owner: owner,
            continuationToken: continuationToken,
            includePlanDetails: includePlanDetails,
            filterActivePlans: filterActivePlans
        };

        return this.beginRequest<Response>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/testplan/Plans/{planId}",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            returnRawResponse: true
        }).then((response: Response) => {
            const continuationToken = response.headers.get(continuationTokenHeaderKey);
            return response.text().then((r) => {
                return {
                    results: deserializeVssJsonObject<TestPlan.TestPlan[]>(r),
                    continuationToken: continuationToken
                } as ExtendedTestPlan.TestArtifactWithContinuationToken<TestPlan.TestPlan>;
            });
        });
    }
}

export const TestPlanClientName = "ITestPlanExtendedRestClient";

export function getTestPlanExtendedClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): ITestPlanExtendedRestClient {
    return pageContext.getRestClient<ITestPlanExtendedRestClient>(TestPlanClientName, options);
}

const continuationTokenHeaderKey = "x-ms-continuationtoken";

RestClients.add(TestPlanClientName, {
    factory: TestPlanExtendedRestClient,
    options: {
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
